<script lang="ts">
    import { onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import { api, cart, config, loader, toast } from "../stores";
    import ProductPrice from "./ProductPrice.svelte";
    import type { CartProduct, Product } from "../model";
    import PastillePromotion from "./PastillePromotion.svelte";

    export let product: Product;
    export const index: number = 0;
    export let crossSell: boolean = false;
    export let replaceHistoryOnClick: boolean = false;
    export let inAdmin: boolean = false;
    export let clickable: boolean = true;
    let operation = null;
    let imgViewerUrl = "";
    let cartProduct: CartProduct;

    const handleImgError = (ev) => (ev.target.src = "/no_visual.jpg");

    $: imgViewerUrl =
        product && product.images && product.images.length > 0
            ? product.images[0].indexOf("data:image") === 0
                ? product.images[0]
                : product.images[0] + "?g=300x300"
            : "";

    onMount(async () => {
        if (!inAdmin) {
            const processId = $loader.show();
            $api.getOperation().then(
                (result) => {
                    operation = result;
                    $loader.hide(processId);
                },
                (err) => {
                    console.error(err);
                    $loader.hide(processId);
                }
            );
        }

        getCart();

        // UPDATE cart product when list change
        $cart.list.subscribe(async (list) => {
            if (product) {
                getCart();
            }
        });
    });

    function getCart(): void {
        if (product) {
            cartProduct = $cart.getProduct(product.uid);
        }
    }

    function onAddToCart(product: Product) {
        if (!inAdmin) {
            $cart.add(product, 1, "shoppingList");
            $toast.success(
                '"' +
                    product.name.toLowerCase() +
                    '" à été ajouté dans votre mémo courses.'
            );
            return false;
        }
    }

    function goToProduct(productUid) {
        if (clickable) {
            if (replaceHistoryOnClick) {
                navigate(productUid, { replace: replaceHistoryOnClick });
            } else {
                navigate("products/" + productUid);
            }
        }
    }
</script>

<div class="product {crossSell ? 'crossSell' : ''}">
    <div class="image-product">
        {#if (product.uid && product.uid !== "") || inAdmin}
            <div
                class="product-thumbnail"
                on:click={() => {
                    goToProduct(product.uid);
                }}
            >
                <img
                    on:error={handleImgError}
                    src={imgViewerUrl}
                    alt="Chargement de l'image:{product.name}"
                />
            </div>
        {/if}
    </div>

    <div
        class="product-header"
        on:click={() => {
            goToProduct(product.uid);
        }}
    >
        <div
            class="product-title"
            on:click={() => {
                goToProduct(product.uid);
            }}
        >
            {#if product.MEA !== "tel"}
                <span class="brand">
                    {#if product.brand}
                        {product.brand}
                    {/if}
                </span> <br />
                <p class="p-title">
                    {product.name}
                    {#if product.displaySupplierRef && product.supplierRef !== ""}
                        {product.supplierRef}
                    {/if}
                </p>
            {/if}
        </div>
    </div>

    <div class="product-pastille">
        <PastillePromotion {product} />
    </div>

    <div class="product-bottom">
        <div class="product-price">
            <ProductPrice {product} customClasses={["mosaic"]} />
        </div>

        {#if product.added || cartProduct?.quantity > 0}
            <div
                class="cart-counter"
                on:click={() => {
                    goToProduct(product.uid);
                }}
            >
                {cartProduct.quantity}
            </div>
        {:else}
            <div
                class="product-add-to-cart"
                on:click={() => {
                    product.added = onAddToCart(product);
                }}
            >
                <span class="icon">
                    <i class="fas fa-shopping-bag" />
                </span>
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @import "../styles/variables.scss";

    .cart-counter {
        position: absolute;
        display: inline-block;
        background-color: $dark-mauve;
        border-radius: 50%;
        color: white;
        width: 50px;
        height: 50px;
        line-height: 50px;
        text-align: center;
        right: 10px;
        bottom: 5px;
        cursor: pointer;
    }
    .product {
        cursor: pointer;
        position: relative;
        display: flex;
        flex-direction: column;
        text-align: center;
        overflow: hidden;
        width: 100%;
        height: 100%;
        border-radius: 12px;
        padding: 1rem;
        background-color: white;
        &.crossSell {
            margin: 1rem;
            border: 1px solid $grey-light;
            height: 30em;
        }
    }

    .product-pastille {
        display: flex;
        margin-top: auto;
        margin-right: auto;
        @media (max-width: $mobile-breakpoint) {
            transform: scale(0.7);
        }
    }

    .product-thumbnail {
        width: 100%;
        height: 15em;
        margin-bottom: 5px;
        img {
            max-height: 100%;
            max-width: 90%;
            &.unavailable {
                opacity: 0.4;
            }
        }

        .unavailable-text {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            margin: auto;
            padding-top: 38%;
            transform: rotate(19deg);
            span {
                font-weight: bold;
                color: $dark-black;
                font-size: 1em;
                background-color: white;
            }
        }
    }

    .product-bottom {
        display: flex;
        flex-direction: row;
        margin-top: auto;
    }

    .product-header {
        display: flex;
        flex-direction: column;
    }

    .product-title {
        text-align: left;
        width: 80%;
        color: black;
        display: inline-block;
        padding-left: 5px;
        .brand {
            font-family: "Gilmer Bold";
            font-size: 1.2rem;
            font-weight: bold;
            height: 16px;
            text-transform: upperccase;
        }
        .p-title {
            text-transform: uppercase;
            font-family: "Gilmer Medium";
            font-size: 1rem;
            font-weight: normal;
            overflow: hidden;
        }
    }

    .product-add-to-cart {
        display: inline-block;
        background-color: $dark-mauve;
        border-radius: 50%;
        width: 50px;
        height: 50px;
        cursor: pointer;
        position: absolute;
        right: 0.8rem;
        bottom: 1rem;
        display: flex;
        border: 1px $dark-mauve solid;

        span {
            color: white;
        }
        .icon {
            margin: auto;
        }
    }

    .webview-check {
        color: green;
        margin-top: 20px;
    }
</style>
